/* ============================================================
   GALERIJ: tabs, carousel en snelbestellen.

     c-gallery-container  de tabs; zet telkens één lijst op is-active
     c-gallery-list       één Embla-carousel met pijlen en voortgangsbalk
     c-product-card       hover tussen het eerste en tweede beeld
     c-product-form       'Add to bag' zonder de pagina te herladen

   In een eigen functie: alle themascripts delen één globale scope en een
   dubbele naam legt anders het hele bestand stil. Embla staat in
   assets/embla.js (UMD, zet window.EmblaCarousel) en laadt eerder.
   ============================================================ */

(function () {
  'use strict';

  var rustig = function () { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; };

  /* ---------- één carousel ---------- */

  class GalleryList extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.embla__viewport');
      this.balk = this.querySelector('.embla__progress__bar');
      this.vorige = this.querySelector('.embla__prev');
      this.volgende = this.querySelector('.embla__next');
      if (!this.viewport || !window.EmblaCarousel) return;
      if (this.closest('.gallery-container.v-grid')) return;

      this.embla = window.EmblaCarousel(this.viewport, {
        align: 'start',
        containScroll: 'trimSnaps',
        loop: false,
        duration: rustig() ? 0 : 25,
      });

      this.vorige?.addEventListener('click', () => this.embla.scrollPrev());
      this.volgende?.addEventListener('click', () => this.embla.scrollNext());
      this.embla.on('select', () => this.teken());
      this.embla.on('scroll', () => this.schuif());
      this.embla.on('reInit', () => this.teken());
      this.teken();
      this.wijsDeKnopAan();
    }

    disconnectedCallback() { this.embla?.destroy(); this.kijker?.disconnect(); }

    wijsDeKnopAan() {
      const eerste = this.querySelector('.card-product-quick-buy__container');
      if (!eerste) return;
      this.kijker = new IntersectionObserver((rijen) => {
        for (const rij of rijen) {
          if (rij.isIntersecting) {
            eerste.classList.add('is-animate-quick-buy');
            this.kijker.disconnect();
          }
        }
      }, { threshold: 0.25 });
      this.kijker.observe(this);
    }

    herstart() { this.embla?.reInit(); this.teken(); }

    teken() {
      if (!this.embla) return;
      const voortgang = Math.max(0, Math.min(1, this.embla.scrollProgress()));
      if (this.balk) {
        const totaalSlides = this.embla.slideNodes().length || 1;
        const stops = this.embla.scrollSnapList().length || 1;
        const inBeeld = Math.max(1, totaalSlides - stops + 1);
        const deel = Math.min(1, inBeeld / totaalSlides);
        this.balk.style.width = `${(deel * 100).toFixed(4)}%`;
        const speling = deel > 0 ? (1 / deel - 1) * 100 : 0;
        this.balk.style.transform = `translate3d(${(voortgang * speling).toFixed(4)}%,0px,0px)`;
      }
      if (this.vorige) this.vorige.disabled = !this.embla.canScrollPrev();
      if (this.volgende) this.volgende.disabled = !this.embla.canScrollNext();
    }

    schuif() {
      if (!this.embla || !this.balk) return;
      const voortgang = Math.max(0, Math.min(1, this.embla.scrollProgress()));
      const deel = parseFloat(this.balk.style.width) / 100 || 1;
      const speling = deel > 0 ? (1 / deel - 1) * 100 : 0;
      this.balk.style.transform = `translate3d(${(voortgang * speling).toFixed(4)}%,0px,0px)`;
    }
  }

  /* ---------- de tabs eromheen ---------- */

  class GalleryContainer extends HTMLElement {
    connectedCallback() {
      this.lijsten = [...this.querySelectorAll('c-gallery-list')];
      this.knoppen = [...this.querySelectorAll('.tabs .link')];
      if (!this.lijsten.length) return;
      if (!this.knoppen.length) {
        this.lijsten.forEach((l) => l.classList.add('is-active'));
        return;
      }
      this.toon(this.knoppen[0].dataset.id);
      this.knoppen.forEach((knop) =>
        knop.addEventListener('click', () => this.toon(knop.dataset.id)));
    }

    toon(id) {
      this.knoppen.forEach((k) => k.classList.toggle('is-active', k.dataset.id === id));
      this.lijsten.forEach((lijst) => {
        const aan = lijst.dataset.id === id;
        lijst.classList.toggle('is-active', aan);
        if (aan) lijst.herstart?.();
      });
    }
  }

  /* ---------- productkaart ---------- */

  class ProductCard extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('.card-product_image');
    }
  }

  /* ---------- snelbestellen ---------- */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.knop = this.querySelector('button[type="submit"]');
      this.form?.addEventListener('submit', (e) => this.verstuur(e));
    }

    async verstuur(e) {
      e.preventDefault();
      if (this.bezig) return;
      this.bezig = true;
      this.knop?.setAttribute('data-gift-pending', '');
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(res.status);
        const data = await res.json().catch(() => null);
        const regel = data && (data.items ? data.items[0] : data);

        /* cart:updated laat de lade zijn inhoud ophalen, cart:added opent hem. */
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:added', { detail: {
          titel: regel && (regel.product_title || regel.title) || '',
          beeld: regel && (regel.image || regel.featured_image && regel.featured_image.url) || '',
          prijs: this.querySelector('[data-product-price]')?.textContent.trim() || '',
        } }));
      } catch (err) {
        this.meld(this.dataset.errorMessage || 'Something went wrong.');
      } finally {
        this.bezig = false;
        this.knop?.removeAttribute('data-gift-pending');
      }
    }

    meld(tekst) {
      let vak = this.querySelector('.product-form__error');
      if (!vak) {
        vak = document.createElement('div');
        vak.className = 'product-form__error';
        vak.setAttribute('role', 'alert');
        this.appendChild(vak);
      }
      vak.textContent = tekst;
      vak.hidden = !tekst;
    }
  }

  var lijst = [
    ['c-gallery-list', GalleryList],
    ['c-gallery-container', GalleryContainer],
    ['c-product-card', ProductCard],
    ['c-product-form', ProductForm],
    ['c-media-card', class extends HTMLElement {}],
    ['c-section-text-gallery-media', class extends HTMLElement {}],
  ];
  for (var i = 0; i < lijst.length; i++) {
    if (!customElements.get(lijst[i][0])) customElements.define(lijst[i][0], lijst[i][1]);
  }
})();
